import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from '@angular/router';
import { AppState } from "@app/reducers";
import {
  BreadCrumbModel,
  CustomDirectiveConfig,
  HttpCancelService,
  IApplicationResponse,
  LoggedInUserModel, ModulesBasedApiSuffix,
  ResponseMessageTypes,
  SnackbarService,
  clearFormControlValidators,
  enableFormControls,
  prepareGetRequestHttpParams,
  prepareRequiredHttpParams,
  setRequiredValidator
} from "@app/shared";
import { CrudService, RxjsService } from "@app/shared/services";
import { loggedInUserData, selectDynamicEagerLoadingMotivationReasonTypesState$, selectDynamicEagerLoadingRewardPartnershipsState$, selectStaticEagerLoadingFixedContractFeePeriodsState$ } from '@modules/others';
import { CampaignModuleApiSuffixModels } from "@modules/others/configuration/components/campaign-management/shared/enums/campaign.enum";
import { ReasonForMotivationTypes } from "@modules/others/configuration/models/reason-for-motivation-model";
import {
  LeadItemPriceAdjustments,
  LeadServicePriceAdjustments, PriceAdjustModel, SalesModuleApiSuffixModels,
  ServiceAgreementStepsParams, StepperParams,
  btnActionTypes,
  selectLeadCreationStepperParamsState$, selectLeadHeaderDataState$
} from "@modules/sales";
import { Store } from "@ngrx/store";
import { Observable, combineLatest, forkJoin } from "rxjs";
import { ReusablePrimeNGTableFeatureService } from '../../../../../../shared/services/reusable-primeng-table-features.service';
@Component({
  selector: "app-adjust-cost-info",
  templateUrl: "./adjust-cost-info.component.html"
})
export class AdjustCostInfoComponent {
  @Input() params: StepperParams;
  @Output() emittedEvent = new EventEmitter<StepperParams>();
  @ViewChild('itemModal', { static: false }) itemModal: ElementRef;
  @ViewChild('rewardPartnership', { static: false }) rewardPartnership: ElementRef;
  @ViewChild('discretionaryDiscountReason', { static: false }) discretionaryDiscountReason: ElementRef;
  @ViewChild('marketingCampaignSelect', { static: false }) marketingCampaignSelect: ElementRef;
  leadServicePriceAdjustments: FormArray;
  leadItemPriceAdjustments: FormArray;
  loggedInUserData: LoggedInUserModel;
  priceAdjustmentForm: FormGroup;
  leadId;
  leadHeaderData;
  levyInfo = {};
  complmPeriods = [];
  fixedPeriods = [];
  annualPeriods = [];
  detailsData;
  isADecimalOnly = new CustomDirectiveConfig({ isADecimalOnly: true, shouldPasteKeyboardEventBeRestricted: true });
  rewardPartneships = [];
  motivationReasons = [];
  isGene: boolean = false;
  isLSS: boolean = false;
  isCamp: boolean = false;
  isReward: boolean = false;
  isLicenseTypeEnabled: boolean = false;
  rewardId: string = '';
  serviceAgreementStepsParams: ServiceAgreementStepsParams;
  doaDetails: any = {
    doaApprovalLevel: 0,
    doaLevelConfigId: ''
  };
  licenceTypes = [];
  breadCrumb: BreadCrumbModel;
  campaignList = []
  marketingCampaignId;
  extraClassNames = "";
  doaApprovalLevelDetail: string;
  shouldShowContainer = false;
  isWaiverNetworkFee: any = null;

  constructor(
    private crudService: CrudService,
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private snackbarService: SnackbarService,
    private router: Router,
    private rxjsService: RxjsService,
    private store: Store<AppState>, private reusablePrimeNGTableFeatureService: ReusablePrimeNGTableFeatureService,
    private httpCancelService: HttpCancelService
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      this.leadId = params.leadId;
    })
  }

  ngOnInit(): void {
    this.combineLatestNgrxStoreData();
    this.createPriceAdjustmentForm();
    this.forkJoinRequests();
    this.onFormControlChanges();
  }

  combineLatestNgrxStoreData() {
    combineLatest([
      this.store.select(loggedInUserData),
      this.store.select(selectLeadHeaderDataState$),
      this.store.select(selectLeadCreationStepperParamsState$),
      this.store.select(selectStaticEagerLoadingFixedContractFeePeriodsState$),
      this.store.select(selectDynamicEagerLoadingRewardPartnershipsState$),
      this.store.select(selectDynamicEagerLoadingMotivationReasonTypesState$)]
    ).subscribe((response) => {
      this.loggedInUserData = new LoggedInUserModel(response[0]);
      this.leadHeaderData = response[1];
      this.leadId = this.leadHeaderData.leadId;
      this.serviceAgreementStepsParams = new ServiceAgreementStepsParams(response[2]);
      this.extraClassNames = this.serviceAgreementStepsParams.quotationVersionId ? 'disabled' : "";
      this.breadCrumb = {
        pageTitle: { key: 'Adjust Cost', value: this.leadHeaderData.leadRefNo },
        items: [{
          key: this.leadHeaderData.leadRefNo, routeUrl: 'landingPage'
        }, { key: 'Adjust Cost' }]
      };
      this.fixedPeriods = response[3];
      this.rewardPartneships = response[4];
      this.motivationReasons = response[5]?.filter(r => r.displayName == ReasonForMotivationTypes.GENERAL_DISCOUNT).map(mR => mR.motivationReasons)[0];
    });
  }

  onFormControlChanges() {
    this.priceAdjustmentForm?.get('rewardId').valueChanges.subscribe((rewardId) => {
      this.onAfterRewardId(rewardId);
    });
    this.priceAdjustmentForm?.get('marketingCampaignId').valueChanges.subscribe((marketingCampaignId) => {
      this.onAfterCampaignId(marketingCampaignId);
    });
  }

  onAfterRewardId(rewardId) {
    if ((this.priceAdjustmentForm?.get('isReward').value && rewardId) || (this.priceAdjustmentForm?.get('isCamp').value &&
      this.priceAdjustmentForm?.get('marketingCampaignId').value) || (this.priceAdjustmentForm?.get('isGene').value) ||
      (this.priceAdjustmentForm?.get('isLSS').value)) {
      this.shouldShowContainer = true;
    }
    else {
      this.shouldShowContainer = false;
    }
  }

  onAfterCampaignId(marketingCampaignId) {
    if ((this.priceAdjustmentForm?.get('isCamp').value && marketingCampaignId) ||
      (this.priceAdjustmentForm?.get('isReward').value && this.priceAdjustmentForm?.get('rewardId').value) ||
      (this.priceAdjustmentForm?.get('isGene').value) || (this.priceAdjustmentForm?.get('isLSS').value)) {
      this.shouldShowContainer = true;
    }
    else {
      this.shouldShowContainer = false;
    }
  }

  changeServicePercentage(index) {
    if (this.getleadServicePriceAdjustments.controls[index].get("isDiscountPercentage").value == true) {
      this.getleadServicePriceAdjustments.controls[index].get("isDiscountAmount").setValue(false)
      this.getleadServicePriceAdjustments.controls[index].get("discountAmount").setValue("", { emitEvent: false });
      this.getleadServicePriceAdjustments.controls[index].get("discountAmount").setValidators([Validators.max(100)]);
      this.getleadServicePriceAdjustments.controls[index].get("discountAmount").updateValueAndValidity();
    }
    if (!this.getleadServicePriceAdjustments.controls[index].get('discountAmount').value) {
      this.getleadServicePriceAdjustments.controls[index].get('discountAmount').setValue(0, { emitEvent: false });
    }
    this.onPercentageChanges(index, 'service');
    if (this.getleadServicePriceAdjustments.controls[index].get('discountAmount').value) {
      this.onAfterServiceValueChanges(index, this.getleadServicePriceAdjustments.controls[index].get('discountAmount').value);
    } else {
      this.getOriginalAmt(index, 'service');
    }
  }

  changeServiceAmount(index) {
    if (this.getleadServicePriceAdjustments.controls[index].get("isDiscountAmount").value == true) {
      this.getleadServicePriceAdjustments.controls[index].get("isDiscountPercentage").setValue(false);
      this.getleadServicePriceAdjustments.controls[index].get("discountAmount").setValue("", { emitEvent: false });
      this.getleadServicePriceAdjustments.controls[index].get("discountAmount").clearValidators();
      this.getleadServicePriceAdjustments.controls[index].get("discountAmount").updateValueAndValidity();
    }
    if (!this.getleadServicePriceAdjustments.controls[index].get('discountAmount').value) {
      this.getleadServicePriceAdjustments.controls[index].get('discountAmount').setValue(0, { emitEvent: false });
    }
    this.onPercentageChanges(index, 'service');
    if (this.getleadServicePriceAdjustments.controls[index].get('discountAmount').value) {
      this.onAfterServiceValueChanges(index, this.getleadServicePriceAdjustments.controls[index].get('discountAmount').value);
    } else {
      this.getOriginalAmt(index, 'service');
    }
  }

  changeAmount(index) {
    if (this.getleadItemPriceAdjustments.controls[index].get("isDiscountAmount").value == true) {
      this.getleadItemPriceAdjustments?.controls[index].get("isDiscountPercentage").setValue(false);
      this.getleadItemPriceAdjustments?.controls[index].get("discountAmount").setValue("", { emitEvent: false });
      this.getleadItemPriceAdjustments?.controls[index]?.get("discountAmount")?.clearValidators();
      this.getleadItemPriceAdjustments?.controls[index]?.get("discountAmount")?.updateValueAndValidity();
    }
    if (!this.getleadItemPriceAdjustments.controls[index].get('discountAmount').value) {
      this.getleadItemPriceAdjustments.controls[index].get('discountAmount').setValue(0, { emitEvent: false });
    }
    this.onPercentageChanges(index, 'item');
    if (this.getleadItemPriceAdjustments.controls[index].get('discountAmount').value) {
      this.onAfterItemValueChanges(index, this.getleadItemPriceAdjustments.controls[index].get('discountAmount').value);
    } else {
      this.getOriginalAmt(index, 'item');
    }
  }

  changePercentage(index) {
    if (this.getleadItemPriceAdjustments.controls[index].get("isDiscountPercentage").value == true) {
      this.getleadItemPriceAdjustments.controls[index].get("isDiscountAmount").setValue(false);
      this.getleadItemPriceAdjustments.controls[index].get("discountAmount").setValue("", { emitEvent: false });
      this.getleadItemPriceAdjustments.controls[index].get("discountAmount").setValidators([Validators.max(100)]);
      this.getleadItemPriceAdjustments.controls[index].get("discountAmount").updateValueAndValidity();
    }
    this.onPercentageChanges(index, 'item');
    if (!this.getleadItemPriceAdjustments.controls[index].get('discountAmount').value) {
      this.getleadItemPriceAdjustments.controls[index].get('discountAmount').setValue(0, { emitEvent: false });
    }
    if (this.getleadItemPriceAdjustments.controls[index].get('discountAmount').value) {
      this.onAfterItemValueChanges(index, this.getleadItemPriceAdjustments.controls[index].get('discountAmount').value);
    } else {
      this.getOriginalAmt(index, 'item');
    }
  }

  onPercentageChanges(i, str) {
    if (str == 'item') {
      this.getleadItemPriceAdjustments.controls[i].get('discountAmount').valueChanges.subscribe((levyPercentage: any) => {
        if (levyPercentage != 0) {
          this.onAfterItemValueChanges(i, levyPercentage);
        } else {
          this.getOriginalAmt(i, 'item');
        }
      });
    } else {
      this.getleadServicePriceAdjustments.controls[i].get('discountAmount').valueChanges.subscribe((levyPercentage: any) => {
        if (levyPercentage != 0) {
          this.onAfterServiceValueChanges(i, levyPercentage);
        } else {
          this.getOriginalAmt(i, 'service');
        }
      });
    }
  }

  onAfterItemValueChanges(i, levyPercentage) {
    let isDiscountAmount = this.getleadItemPriceAdjustments.controls[i].get('isDiscountAmount').value;
    let isDiscountPercentage = this.getleadItemPriceAdjustments.controls[i].get('isDiscountPercentage').value;
    let discountValue = +this.getleadItemPriceAdjustments.controls[i].get('discountAmount').value;
    let subtotal = this.getleadItemPriceAdjustments.controls[i].get('subTotal').value;
    if (isDiscountAmount) {
      this.getleadItemPriceAdjustments.controls[i].get("discountAmount").setValidators([Validators.max(subtotal)]);
    }
    let totalAmount = this.getleadItemPriceAdjustments.controls[i].get('totalAmount').value;
    let vatAmount = this.getleadItemPriceAdjustments.controls[i].get('vatAmount').value;
    let adjustedTotal = this.getleadItemPriceAdjustments.controls[i].get('adjustedTotal').value;
    if (isDiscountPercentage) {
      discountValue = (discountValue * parseFloat(subtotal)) / 100;
    }
    adjustedTotal = subtotal - discountValue;
    vatAmount = (adjustedTotal * this.detailsData?.taxPercentage) / 100;
    totalAmount = +parseFloat(adjustedTotal)?.toFixed(2) + +parseFloat(vatAmount)?.toFixed(2);
    this.getleadItemPriceAdjustments.controls[i].get('discountValue').setValue(discountValue != 0 ? discountValue.toFixed(2) : discountValue);
    this.getleadItemPriceAdjustments.controls[i].get('totalAmount').setValue(parseFloat(totalAmount).toFixed(2));
    this.getleadItemPriceAdjustments.controls[i].get('vatAmount').setValue(parseFloat(vatAmount).toFixed(2));
    this.getleadItemPriceAdjustments.controls[i].get('adjustedTotal').setValue(parseFloat(adjustedTotal).toFixed(2));
    if (levyPercentage === '00' || levyPercentage === '0.0' || levyPercentage === '0.00' || levyPercentage === '00.0'
      || levyPercentage === '00.00' || levyPercentage === '00.' || levyPercentage === '0.') {
      this.getleadItemPriceAdjustments.controls[i].get('discountAmount').setValue(0, { emitEvent: false });
    }
  }

  onAfterServiceValueChanges(i, levyPercentage) {
    let isDiscountPercentage = this.getleadServicePriceAdjustments.controls[i].get('isDiscountPercentage').value;
    let isDiscountAmount = this.getleadServicePriceAdjustments.controls[i].get('isDiscountAmount').value;
    let discountValue = +this.getleadServicePriceAdjustments.controls[i].get('discountAmount').value;
    let subtotal = this.getleadServicePriceAdjustments.controls[i].get('subTotal').value;
    if (isDiscountAmount) {
      this.getleadServicePriceAdjustments.controls[i].get("discountAmount").setValidators([Validators.max(subtotal)]);
    }
    let totalAmount = this.getleadServicePriceAdjustments.controls[i].get('totalAmount').value;
    let vatAmount = this.getleadServicePriceAdjustments.controls[i].get('vatAmount').value;
    let adjustedTotal = this.getleadServicePriceAdjustments.controls[i].get('adjustedTotal').value;
    if (isDiscountPercentage) {
      discountValue = (discountValue * parseFloat(subtotal)) / 100;
    }
    adjustedTotal = subtotal - discountValue;
    vatAmount = (adjustedTotal * this.detailsData?.taxPercentage) / 100;
    totalAmount = +parseFloat(adjustedTotal)?.toFixed(2) + +parseFloat(vatAmount)?.toFixed(2);
    this.getleadServicePriceAdjustments.controls[i].get('discountValue').setValue(discountValue != 0 ? discountValue.toFixed(2) : discountValue);
    this.getleadServicePriceAdjustments.controls[i].get('totalAmount').setValue(parseFloat(totalAmount).toFixed(2));
    this.getleadServicePriceAdjustments.controls[i].get('vatAmount').setValue(parseFloat(vatAmount).toFixed(2));
    this.getleadServicePriceAdjustments.controls[i].get('adjustedTotal').setValue(parseFloat(adjustedTotal).toFixed(2));
    if (levyPercentage === '00' || levyPercentage === '0.0' || levyPercentage === '0.00' || levyPercentage === '00.0'
      || levyPercentage === '00.00' || levyPercentage === '00.' || levyPercentage === '0.') {
      this.getleadServicePriceAdjustments.controls[i].get('discountAmount').setValue(0, { emitEvent: false });
    }
  }

  onAmtBlur(i, str) {
    if (str == 'item') {
      if (!this.getleadItemPriceAdjustments.controls[i].get("discountAmount").value || this.getleadItemPriceAdjustments.controls[i].get("discountAmount").value == 0) {
        this.getleadItemPriceAdjustments.controls[i].get('discountAmount').setValue(0, { emitEvent: false });
      }
    } else {
      if (!this.getleadServicePriceAdjustments.controls[i].get("discountAmount").value || this.getleadServicePriceAdjustments.controls[i].get("discountAmount").value == 0) {
        this.getleadServicePriceAdjustments.controls[i].get('discountAmount').setValue(0, { emitEvent: false });
      }
    }
  }

  getOriginalAmt(i, str) {
    if (str == 'item') {
      this.getleadItemPriceAdjustments.controls[i].get('discountValue').setValue(this.detailsData.leadItemPriceAdjustments[i].discountValue)
      this.getleadItemPriceAdjustments.controls[i].get('adjustedTotal').setValue(this.detailsData.leadItemPriceAdjustments[i].adjustedTotal)
      this.getleadItemPriceAdjustments.controls[i].get('vatAmount').setValue(this.detailsData.leadItemPriceAdjustments[i].vatAmount)
      this.getleadItemPriceAdjustments.controls[i].get('totalAmount').setValue(this.detailsData.leadItemPriceAdjustments[i].totalAmount)
    } else {
      this.getleadServicePriceAdjustments.controls[i].get('discountValue').setValue(this.detailsData.leadServicePriceAdjustments[i].discountValue)
      this.getleadServicePriceAdjustments.controls[i].get('adjustedTotal').setValue(this.detailsData.leadServicePriceAdjustments[i].adjustedTotal)
      this.getleadServicePriceAdjustments.controls[i].get('vatAmount').setValue(this.detailsData.leadServicePriceAdjustments[i].vatAmount)
      this.getleadServicePriceAdjustments.controls[i].get('totalAmount').setValue(this.detailsData.leadServicePriceAdjustments[i].totalAmount)
    }
  }

  forkJoinRequests(): void {
    forkJoin([
      this.crudService.get(ModulesBasedApiSuffix.SALES, SalesModuleApiSuffixModels.SALES_API_ANNUAL_COMPLI_PERIODS, undefined, true, null),
      this.crudService.get(ModulesBasedApiSuffix.SALES, SalesModuleApiSuffixModels.SALES_API_ANNUAL_PERIODS, undefined, true, null),
      this.crudService.get(ModulesBasedApiSuffix.SALES, CampaignModuleApiSuffixModels.UX_MARKETING_CAMPAIGN, undefined, true, null),
      this.crudService.get(ModulesBasedApiSuffix.SALES, SalesModuleApiSuffixModels.UX_LICENCE_TYPES, undefined, true, prepareRequiredHttpParams({
        districtId: this.leadHeaderData.districtId
      }))]
    ).subscribe((response: IApplicationResponse[]) => {
      response.forEach((resp: IApplicationResponse, ix: number) => {
        if (resp.isSuccess && resp.statusCode === 200) {
          switch (ix) {
            case 0:
              this.complmPeriods = resp.resources;
              break;
            case 1:
              this.annualPeriods = resp.resources;
              break;
            case 2:
              this.campaignList = resp.resources;
              break;
            case 3:
              this.licenceTypes = resp.resources;
              this.getPriceAdjustmentByLeadId();
              break;
          }
        }
      });
    });
  }

  createPriceAdjustmentForm() {
    let priceAdjustModel = new PriceAdjustModel();
    this.priceAdjustmentForm = this.formBuilder.group({
      leadServicePriceAdjustments: this.formBuilder.array([]),
      leadItemPriceAdjustments: this.formBuilder.array([])
    });
    Object.keys(priceAdjustModel).forEach((key) => {
      this.priceAdjustmentForm.addControl(key, new FormControl(priceAdjustModel[key]));
    });
    this.priceAdjustmentForm = setRequiredValidator(this.priceAdjustmentForm, ["licenseTypeId", "motivationReasonId"]);
  }

  get getleadServicePriceAdjustments(): FormArray {
    if (!this.priceAdjustmentForm) return;
    return this.priceAdjustmentForm.get("leadServicePriceAdjustments") as FormArray;
  }

  get getleadItemPriceAdjustments(): FormArray {
    if (!this.priceAdjustmentForm) return;
    return this.priceAdjustmentForm.get("leadItemPriceAdjustments") as FormArray;
  }

  createleadServicePriceAdjustments(leadServicePriceAdjustments?: LeadServicePriceAdjustments): FormGroup {
    let leadServicePriceAdjustmentsModel = new LeadServicePriceAdjustments(leadServicePriceAdjustments);
    let formControls = {};
    Object.keys(leadServicePriceAdjustmentsModel).forEach((key) => {
      if (key === 'discountAmount') {
        formControls[key] = [{ value: leadServicePriceAdjustmentsModel[key], disabled: false }]
      } else {
        formControls[key] = [{ value: leadServicePriceAdjustmentsModel[key], disabled: false }]
      }
    });
    return this.formBuilder.group(formControls);
  }

  createleadItemPriceAdjustments(leadItemPriceAdjustments?: LeadItemPriceAdjustments): FormGroup {
    let leadItemPriceAdjustmentsFormControlModel = new LeadItemPriceAdjustments(leadItemPriceAdjustments);
    let formControls = {};
    Object.keys(leadItemPriceAdjustmentsFormControlModel).forEach((key) => {
      if (key === 'discountAmount') {
        formControls[key] = [{ value: leadItemPriceAdjustmentsFormControlModel[key], disabled: false }]
      } else {
        formControls[key] = [{ value: leadItemPriceAdjustmentsFormControlModel[key], disabled: false }]
      }
    });
    return this.formBuilder.group(formControls);
  }

  getLeadDetailsByLeadId(): Observable<IApplicationResponse> {
    return this.crudService.get(ModulesBasedApiSuffix.SALES, SalesModuleApiSuffixModels.SALES_API_LEADS_HEADER, this.leadId, false, null, 1)
  }

  convertTodecimal(obj) {
    obj.adjustedTotal = obj.adjustedTotal ? parseFloat(obj.adjustedTotal).toFixed(2) : 0;
    obj.discountValue = obj.discountValue ? parseFloat(obj.discountValue).toFixed(2) : 0;
    obj.subTotal = obj.subTotal ? parseFloat(obj.subTotal).toFixed(2) : 0;
    obj.totalAmount = obj.totalAmount ? parseFloat(obj.totalAmount).toFixed(2) : 0;
    obj.vatAmount = obj.vatAmount ? parseFloat(obj.vatAmount).toFixed(2) : 0;
    return obj;
  }

  getPriceAdjustmentByLeadId() {
    if (this.leadItemPriceAdjustments) {
      this.leadItemPriceAdjustments.clear();
    }
    if (this.leadServicePriceAdjustments) {
      this.leadServicePriceAdjustments.clear();
    }
    let prepareGetRequestHttpParamsObj = {
      leadId: this.leadId,
      quotationVersionId: this.serviceAgreementStepsParams?.quotationVersionId,
      isRewardPartnership: this.priceAdjustmentForm.get('isReward').value,
      rewardPartnershipId: this.priceAdjustmentForm.get('rewardId').value,
      isCampaign: this.priceAdjustmentForm.get('isCamp').value,
      marketingCampaignId: this.priceAdjustmentForm.get('isCamp').value == true ? this.priceAdjustmentForm.get('marketingCampaignId').value : "",
      isLSSPreferentialRate: this.priceAdjustmentForm.get('isLSS').value
    }
    this.rxjsService.setGlobalLoaderProperty(true);
    this.crudService.get(ModulesBasedApiSuffix.SALES, SalesModuleApiSuffixModels.SALES_API_PRICE_ADJUSTMENTS, undefined, false,
      prepareGetRequestHttpParams(null, null, prepareGetRequestHttpParamsObj)).subscribe(resp => {
        if (this.leadServicePriceAdjustments) {
          this.leadServicePriceAdjustments.clear();
        }
        if (this.leadItemPriceAdjustments) {
          this.leadItemPriceAdjustments.clear();
        }
        this.detailsData = resp.resources;
        this.doaApprovalLevelDetail = this.detailsData.doaApprovalLevel ?
          `${this.detailsData.doaApprovalLevel + " ( " + this.detailsData.doaApprovalLevelName} )` : '-';
        if (this.detailsData) {
          if (this.detailsData.isLicenseTypeEnabled) {
            this.isLicenseTypeEnabled = true;
            let licenseTypeId = this.licenceTypes?.find(lT => lT['licenseTypeName'] == 'Default' || lT['licenseTypeName'] == 'DEFAULT')?.licenseTypeId;
            if (licenseTypeId) {
              this.detailsData.licenseTypeId = licenseTypeId;
            }
          } else {
            this.isLicenseTypeEnabled = false;
            this.priceAdjustmentForm.get('licenseTypeId').enable();
            this.priceAdjustmentForm = clearFormControlValidators(this.priceAdjustmentForm, ['licenseTypeId']);
          }
          if (this.doaDetails?.['doaApprovalLevel']) {
            this.doaDetails['doaApprovalLevel'] = this.detailsData.doaApprovalLevel;
          }
          if (this.doaDetails?.['doaLevelConfigId']) {
            this.doaDetails['doaLevelConfigId'] = this.detailsData.doaLevelConfigId;
          }
          if (!this.detailsData.annualNetworkFeePeriodId) {
            this.detailsData.annualNetworkFeePeriodId = "";
          }
          if (!this.detailsData.complimentaryMonthId) {
            this.detailsData.complimentaryMonthId = "";
          }
          if (!this.detailsData.fixedContractFeePeriodId) {
            this.detailsData.fixedContractFeePeriodId = "";
          }
          if (!this.detailsData.motivationReasonId) {
            this.detailsData.motivationReasonId = "";
          }
          if (!this.detailsData.rewardPartnershipId) {
            this.detailsData.rewardPartnershipId = "";
          }
          if (!this.detailsData.marketingCampaignId) {
            this.detailsData.marketingCampaignId = "";
          }
          if (this.detailsData.isRewardPartnership == false && this.detailsData.isLSSPreferentialRate == false && this.detailsData.isCampaign == false) {
            this.detailsData.isGeneralDiscount = true;
          }
          let priceAdjustModel = new PriceAdjustModel(this.detailsData);
          // let copiedDetails = removeProperyFromAnObjectWithDeepCopy(priceAdjustModel, [' ']); // As per Ashok and Divya conversation it should be auto populated. (april-12,2024)
          this.priceAdjustmentForm.patchValue(priceAdjustModel, { emitEvent: false });
          this.onAfterRewardId(this.priceAdjustmentForm.get('rewardId').value);
          this.onAfterCampaignId(this.priceAdjustmentForm.get('marketingCampaignId').value);
          if (priceAdjustModel.rewardPartnershipRefNo) {
            this.priceAdjustmentForm.get('rewardPartnershipRefNo').disable();
          } else {
            this.priceAdjustmentForm.get('rewardPartnershipRefNo').enable();
          }
          this.priceAdjustmentForm.get('isCamp').setValue(this.detailsData.isCampaign);
          this.priceAdjustmentForm.get('isGene').setValue(this.detailsData.isGeneralDiscount);
          this.priceAdjustmentForm.get('isLSS').setValue(this.detailsData.isLSSPreferentialRate);
          this.priceAdjustmentForm.get('isReward').setValue(this.detailsData.isRewardPartnership);
          this.priceAdjustmentForm.get('rewardId').setValue(this.detailsData.rewardPartnershipId);
          if (this.isCamp) {
            this.priceAdjustmentForm.get('isCamp').setValue(this.isCamp);
            this.priceAdjustmentForm.get('isLSS').setValue(this.isLSS);
            this.priceAdjustmentForm.get('isReward').setValue(this.isReward);
            this.priceAdjustmentForm.get('isGene').setValue(this.isGene);
            this.priceAdjustmentForm.get('rewardId').setValue('');
          }
          if (this.isLSS) {
            this.priceAdjustmentForm.get('isCamp').setValue(this.isCamp);
            this.priceAdjustmentForm.get('isLSS').setValue(this.isLSS);
            this.priceAdjustmentForm.get('isReward').setValue(this.isReward);
            this.priceAdjustmentForm.get('isGene').setValue(this.isGene);
            this.priceAdjustmentForm.get('rewardId').setValue('');
          }
          if (this.isReward) {
            this.priceAdjustmentForm.get('isCamp').setValue(this.isCamp);
            this.priceAdjustmentForm.get('isLSS').setValue(this.isLSS);
            this.priceAdjustmentForm.get('isReward').setValue(this.isReward);
            this.priceAdjustmentForm.get('isGene').setValue(this.isGene);
            this.disbleFields();
          }
          if (this.isGene) {
            this.priceAdjustmentForm.get('isCamp').setValue(this.isCamp);
            this.priceAdjustmentForm.get('isLSS').setValue(this.isLSS);
            this.priceAdjustmentForm.get('isReward').setValue(this.isReward);
            this.priceAdjustmentForm.get('isGene').setValue(this.isGene);
            this.priceAdjustmentForm.get('rewardId').setValue('');
          }
          if (this.rewardId) {
            this.priceAdjustmentForm.get('rewardId').setValue(this.rewardId);
          }
          if (this.marketingCampaignId && this.priceAdjustmentForm.get('isCamp').value == true) {
            this.priceAdjustmentForm.get('marketingCampaignId').setValue(this.marketingCampaignId);
          }
          if (this.detailsData.leadServicePriceAdjustments && this.detailsData.leadServicePriceAdjustments.length > 0) {
            this.leadServicePriceAdjustments = this.getleadServicePriceAdjustments;
            this.detailsData.leadServicePriceAdjustments.forEach((obj) => {
              this.convertTodecimal(obj);
              this.leadServicePriceAdjustments.push(this.createleadServicePriceAdjustments(obj));
            });
          }
          if (this.detailsData.leadItemPriceAdjustments && this.detailsData.leadItemPriceAdjustments.length > 0) {
            this.leadItemPriceAdjustments = this.getleadItemPriceAdjustments;
            this.detailsData.leadItemPriceAdjustments.forEach((obj) => {
              this.convertTodecimal(obj);
              this.leadItemPriceAdjustments.push(this.createleadItemPriceAdjustments(obj));
            });
          }
          if (this.detailsData.isRewardPartnership) {
            this.changeisReward();
          }
          if (this.detailsData.isCampaign) {
            this.changeisCamp();
          }
          if (this.detailsData.isLSSPreferentialRate) {
            if (!this.isReward) {
              this.disableReward();
            }
          }
          if (this.detailsData.isGeneralDiscount) {
            if (!this.isReward) {
              this.disableReward();
            }
          }
          this.changeMonths();
          this.changeisFixedContractFee();
          if (this.serviceAgreementStepsParams?.quotationVersionId) {
            this.disbleFields();
          }
        }
        this.rxjsService.setGlobalLoaderProperty(false);
      });
  }

  disbleFields() {
    this.priceAdjustmentForm.get('complimentaryMonthId').disable({ emitEvent: false });
    this.priceAdjustmentForm.get('isComplimentaryMonths').disable({ emitEvent: false });
    this.priceAdjustmentForm.get('fixedContractFeePeriodId').disable({ emitEvent: false });
    this.priceAdjustmentForm.get('isFixedContractFee').disable({ emitEvent: false });
    this.priceAdjustmentForm.get('isWaiverAdminFee').disable({ emitEvent: false });
    this.priceAdjustmentForm.get('isWaiverProRateCharges').disable({ emitEvent: false });
    this.priceAdjustmentForm.get('annualNetworkFeePeriodId').disable({ emitEvent: false });
  }

  enableFields() {
    this.priceAdjustmentForm = enableFormControls(this.priceAdjustmentForm, ['complimentaryMonthId', 'isComplimentaryMonths', 'fixedContractFeePeriodId',
      'isFixedContractFee', 'isWaiverAdminFee', 'isWaiverProRateCharges', 'annualNetworkFeePeriodId']);
  }

  disableReward() {
    this.priceAdjustmentForm.get('rewardId').setValue("");
    this.priceAdjustmentForm.get('rewardId').clearValidators();
    this.priceAdjustmentForm.get('rewardId').updateValueAndValidity();
    setTimeout(() => {
      this.focusAndBlurRewardPartnership();
      this.priceAdjustmentForm.get('rewardId').disable();
      this.priceAdjustmentForm.get('rewardPartnershipRefNo').disable();
    }, 100);
  }

  changeMonths() {
    if (this.priceAdjustmentForm.get('isComplimentaryMonths').value) {
      this.priceAdjustmentForm.get('complimentaryMonthId').enable();
      this.priceAdjustmentForm.get('complimentaryMonthId').setValidators([Validators.required])
      this.priceAdjustmentForm.get('complimentaryMonthId').updateValueAndValidity();
    } else {
      this.priceAdjustmentForm.get('complimentaryMonthId').markAsUntouched();
      this.priceAdjustmentForm.get('complimentaryMonthId').setValue('')
      this.priceAdjustmentForm.get('complimentaryMonthId').disable({ emitEvent: false });
      this.priceAdjustmentForm.get('complimentaryMonthId').clearValidators();
      this.priceAdjustmentForm.get('complimentaryMonthId').updateValueAndValidity();
    }
  }

  changeisFixedContractFee() {
    this.getDoaConfig();
    if (this.priceAdjustmentForm.get('isFixedContractFee').value) {
      this.priceAdjustmentForm.get('fixedContractFeePeriodId').enable();
      this.priceAdjustmentForm.get('fixedContractFeePeriodId').setValidators([Validators.required])
      this.priceAdjustmentForm.get('fixedContractFeePeriodId').updateValueAndValidity();
    } else {
      this.priceAdjustmentForm.get('fixedContractFeePeriodId').disable();
      this.priceAdjustmentForm.get('fixedContractFeePeriodId').setValue('');
      this.priceAdjustmentForm.get('fixedContractFeePeriodId').clearValidators();
      this.priceAdjustmentForm.get('fixedContractFeePeriodId').updateValueAndValidity();
    }
  }

  changeisReward() {
    if (this.priceAdjustmentForm.get('isReward').value) {
      this.isReward = true;
      this.isCamp = false;
      this.isGene = false;
      this.isLSS = false;
      this.priceAdjustmentForm.get('motivationReasonId').clearValidators();
      this.priceAdjustmentForm.get('motivationReasonId').updateValueAndValidity();
      this.priceAdjustmentForm.get('isGene').setValue(false);
      this.priceAdjustmentForm.get('isLSS').setValue(false);
      this.priceAdjustmentForm.get('isCamp').setValue(false);
      this.priceAdjustmentForm.get('rewardId').enable();
      this.priceAdjustmentForm.get('rewardId').setValidators([Validators.required])
      this.priceAdjustmentForm.get('rewardId').updateValueAndValidity();
      this.focusAndBlurRewardPartnership();
      this.focusAndBlurDiscretionaryDiscountReason();
      this.priceAdjustmentForm.get('marketingCampaignId').setValue("");
      this.priceAdjustmentForm.get('marketingCampaignId').clearValidators();
      this.priceAdjustmentForm.get('marketingCampaignId').updateValueAndValidity();
      this.focusAndBlurMarketingCampaign();
      this.disbleFields();
    } else {
      this.enableFields();
    }
  }

  changeisCamp() {
    if (this.priceAdjustmentForm.get('isCamp').value) {
      this.isCamp = true;
      this.isGene = false;
      this.isLSS = false;
      this.isReward = false;
      this.rewardId = '';
      this.priceAdjustmentForm.get('isReward').setValue(false);
      this.priceAdjustmentForm.get('isGene').setValue(false);
      this.priceAdjustmentForm.get('isLSS').setValue(false);
      this.disableReward();
      this.priceAdjustmentForm.get('rewardPartnershipRefNo').disable();
      this.priceAdjustmentForm.get('marketingCampaignId').setValidators([Validators.required])
      this.priceAdjustmentForm.get('marketingCampaignId').updateValueAndValidity();
      this.priceAdjustmentForm.get('motivationReasonId').clearValidators();
      this.priceAdjustmentForm.get('motivationReasonId').updateValueAndValidity();
      this.focusAndBlurDiscretionaryDiscountReason();
      setTimeout(() => {
        this.focusAndBlurMarketingCampaign();
      }, 100);
    } else {
      this.enableFields();
      this.priceAdjustmentForm.get('marketingCampaignId').setValue("");
      this.priceAdjustmentForm.get('marketingCampaignId').clearValidators();
      this.priceAdjustmentForm.get('marketingCampaignId').updateValueAndValidity();
    }
    this.priceAdjustmentForm.get('rewardPartnershipRefNo').setValue(null);
  }

  changeisGene() {
    if (this.priceAdjustmentForm.get('isGene').value) {
      this.isGene = true;
      this.isLSS = false;
      this.isReward = false;
      this.isCamp = false;
      this.rewardId = '';
      this.priceAdjustmentForm.get('isReward').setValue(false);
      this.priceAdjustmentForm.get('isLSS').setValue(false);
      this.priceAdjustmentForm.get('isCamp').setValue(false);
      this.priceAdjustmentForm.get('motivationReasonId').setValidators([Validators.required]);
      this.priceAdjustmentForm.get('motivationReasonId').updateValueAndValidity();
      this.priceAdjustmentForm.get('marketingCampaignId').setValue("");
      this.priceAdjustmentForm.get('marketingCampaignId').clearValidators();
      this.priceAdjustmentForm.get('marketingCampaignId').updateValueAndValidity();
      this.focusAndBlurMarketingCampaign();
      this.focusAndBlurDiscretionaryDiscountReason();
      this.disableReward();
      this.enableFields();
      this.getPriceAdjustmentByLeadId();
    }
    this.priceAdjustmentForm.get('rewardPartnershipRefNo').setValue(null);
  }

  focusAndBlurRewardPartnership() {
    this.rewardPartnership.nativeElement.focus();
    this.rewardPartnership.nativeElement.blur();
  }

  focusAndBlurDiscretionaryDiscountReason() {
    if (this.priceAdjustmentForm.get('motivationReasonId').disabled) {
      return;
    }
    this.discretionaryDiscountReason.nativeElement.focus();
    this.discretionaryDiscountReason.nativeElement.blur();
  }

  focusAndBlurMarketingCampaign() {
    this.marketingCampaignSelect.nativeElement.focus();
    this.marketingCampaignSelect.nativeElement.blur();
  }

  changeisLSS() {
    if (this.priceAdjustmentForm.get('isLSS').value) {
      this.isLSS = true;
      this.isReward = false;
      this.isGene = false;
      this.isCamp = false;
      this.priceAdjustmentForm.get('motivationReasonId').clearValidators();
      this.priceAdjustmentForm.get('motivationReasonId').updateValueAndValidity();
      this.rewardId = '';
      this.priceAdjustmentForm.get('isReward').setValue(false);
      this.priceAdjustmentForm.get('rewardId').setValue("");
      this.priceAdjustmentForm.get('isGene').setValue(false);
      this.priceAdjustmentForm.get('isCamp').setValue(false);
      this.enableFields();
      this.disableReward();
      this.getPriceAdjustmentByLeadId();
      this.priceAdjustmentForm.get('marketingCampaignId').setValue("");
      this.priceAdjustmentForm.get('marketingCampaignId').clearValidators();
      this.priceAdjustmentForm.get('marketingCampaignId').updateValueAndValidity();
    } else {
      this.enableFields();
    }
    this.priceAdjustmentForm.get('rewardPartnershipRefNo').setValue(null);
  }

  rewardPartnershipIdChange(e) {
    let obj = this.priceAdjustmentForm.get('rewardId').value;
    this.rewardId = obj;
    this.priceAdjustmentForm.get('rewardPartnershipId').setValue(obj)
    let found = this.rewardPartneships.find(e => e.id == obj);
    if (found) {
      this.priceAdjustmentForm.get('isMembershipNumberRequired').setValue(found.isMembershipNumberRequired);
    }
    if (this.priceAdjustmentForm.get('isReward').value) {
      this.priceAdjustmentForm.get('rewardPartnershipRefNo').enable();
    }
    this.getPriceAdjustmentByLeadId();
  }

  campaignIdChange(e) {
    let obj = this.priceAdjustmentForm.get('marketingCampaignId').value;
    this.marketingCampaignId = obj;
    this.getPriceAdjustmentByLeadId();
  }

  addServicesAmt() {
    let dt = this.priceAdjustmentForm.get('leadServicePriceAdjustments').value;
    let sum = 0;
    if (dt && dt.length > 0) {
      if (!this.priceAdjustmentForm.value.rewardId && this.priceAdjustmentForm.value.isReward) {
        sum = 0;
      } else {
        dt.forEach(element => {
          sum = sum + parseFloat(element.discountValue);
        });
      }
      this.priceAdjustmentForm.get('serviceDiscountAmount').setValue(JSON.parse(JSON.stringify(sum.toFixed(2))));
      return 'R ' + (sum).toFixed(2);
    } else {
      return 'R ' + 0;
    }
  }

  addServicesPercent() {
    let selectedServices: any[] = this.priceAdjustmentForm.get('leadServicePriceAdjustments').value;
    let finalTotal: number = 0;
    let totalSubTotal = 0;
    let totalDiscount = 0;
    if (selectedServices?.length > 0) {
      selectedServices.forEach((selectedService, index: number) => {
        if (this.priceAdjustmentForm.value.isGene) {
          if (selectedService.isDiscountAmount) {
            totalDiscount += parseFloat(selectedService.discountAmount);
          }
          else if (selectedService.isDiscountPercentage) {
            totalDiscount += parseFloat(selectedService.discountValue);
          }
          totalSubTotal += parseFloat(selectedService.subTotal);
        } else {
          if (!this.priceAdjustmentForm.value.rewardId && this.priceAdjustmentForm.value.isReward) {
            finalTotal = 0;
          } else {
            totalDiscount += parseFloat(selectedService.discountAmount);
            totalSubTotal += parseFloat(selectedService.subTotal);
          }
        }
        if (index == selectedServices.length - 1) {
          finalTotal = isNaN((totalDiscount / totalSubTotal) * 100) ? 0 : ((totalDiscount / totalSubTotal) * 100);
        }
      });
      this.priceAdjustmentForm.get('serviceDiscountPercentage').setValue((finalTotal).toFixed(2));
      return (finalTotal).toFixed(2) + ' %';
    } else {
      return 0 + ' %';
    }
  }

  addItemAmt() {
    let dt = this.priceAdjustmentForm.get('leadItemPriceAdjustments').value;
    let sum = 0;
    if (dt && dt.length > 0) {
      if (!this.priceAdjustmentForm.value.rewardId && this.priceAdjustmentForm.value.isReward) {
        sum = 0;
      } else {
        dt.forEach(element => {
          sum = sum + parseFloat(element.discountValue);
        });
        this.priceAdjustmentForm.get('itemDiscountAmount').setValue(JSON.parse(JSON.stringify(sum.toFixed(2))));
      }
      return 'R ' + (sum).toFixed(2);
    } else {
      return 'R ' + 0;
    }
  }

  addItemPercent() {
    let selectedItems = this.priceAdjustmentForm.get('leadItemPriceAdjustments').value;
    let finalTotal: number = 0;
    let totalSubTotal = 0;
    let totalDiscount = 0;
    if (selectedItems?.length > 0) {
      selectedItems.forEach((selectedItem, index) => {
        if (this.priceAdjustmentForm.value.isGene) {
          if (selectedItem.isDiscountAmount) {
            totalDiscount += parseFloat(selectedItem.discountAmount);
          }
          else if (selectedItem.isDiscountPercentage) {
            totalDiscount += parseFloat(selectedItem.discountValue);
          }
          totalSubTotal += parseFloat(selectedItem.subTotal);
        }
        else {
          if (!this.priceAdjustmentForm.value.rewardId && this.priceAdjustmentForm.value.isReward) {
            finalTotal = 0;
          } else {
            totalDiscount += parseFloat(selectedItem.discountAmount);
            totalSubTotal += parseFloat(selectedItem.subTotal);
          }
        }
        if (index == selectedItems.length - 1) {
          finalTotal = isNaN((totalDiscount / totalSubTotal) * 100) ? 0 : ((totalDiscount / totalSubTotal) * 100);
        }
      });
      this.priceAdjustmentForm.get('itemDiscountPercentage').setValue((finalTotal).toFixed(2));
      return (finalTotal).toFixed(2) + ' %';
    } else {
      return 0 + ' %';
    }
  }

  onChangeAnnualPeriod() {
    if (this.priceAdjustmentForm.get('annualNetworkFeePeriodId').value) {
      this.isWaiverNetworkFee = true;
    } else {
      this.isWaiverNetworkFee = false;
    }
    this.getDoaConfig()
  }

  getDoaConfig() {
    setTimeout(() => {
      let otherParams = {
        leadId: this.leadId,
        serviceDiscount: this.priceAdjustmentForm.value?.serviceDiscountPercentage,
        itemDiscount: this.priceAdjustmentForm.value?.itemDiscountPercentage,
        servicePrice: this.priceAdjustmentForm.value?.serviceDiscountAmount,
        itemPrice: this.priceAdjustmentForm.value?.itemDiscountAmount,
        userId: this.loggedInUserData?.userId,
        isGeneralDiscount: this.priceAdjustmentForm.value?.isGene ? true : false
      }
      if ((this.serviceAgreementStepsParams?.selectedQuotationVersionIds && this.serviceAgreementStepsParams?.selectedQuotationVersionIds?.split(",")) || this.serviceAgreementStepsParams?.quotationVersionId) {
        otherParams['quotationVersionId'] = this.serviceAgreementStepsParams?.selectedQuotationVersionIds ? this.serviceAgreementStepsParams?.selectedQuotationVersionIds?.split(",") : this.serviceAgreementStepsParams?.quotationVersionId;
      }
      if (this.priceAdjustmentForm.get('isWaiverAdminFee').value?.toString()) {
        otherParams['isWaiverAdminFee'] = this.priceAdjustmentForm.get('isWaiverAdminFee').value;
      }
      if (this.isWaiverNetworkFee?.toString()) {
        otherParams['isWaiverNetworkFee'] = this.priceAdjustmentForm.get('annualNetworkFeePeriodId').value ? true : this.isWaiverNetworkFee;
      }
      if (this.priceAdjustmentForm.get('isWaiverProRateCharges').value?.toString()) {
        otherParams['isWaiverProRataCharges'] = this.priceAdjustmentForm.get('isWaiverProRateCharges').value;
      }
      if (this.priceAdjustmentForm.get('isFixedContractFee').value?.toString()) {
        otherParams['isFixedServicesFee'] = this.priceAdjustmentForm.get('isFixedContractFee').value;
      }

      let serviceDiscount = 0;
      if (this.getleadServicePriceAdjustments?.length) {
        serviceDiscount = Math.max(...this.getleadServicePriceAdjustments.getRawValue()?.map(el => {
          if (el?.isDiscountPercentage && el?.discountAmount) {
            return el?.discountAmount;
          }
          return 0;
        }));
      }
      let installationDiscount = 0;
      if (this.getleadItemPriceAdjustments?.length) {
        installationDiscount = Math.max(...this.getleadItemPriceAdjustments.getRawValue()?.map(el => {
          if (el?.isDiscountPercentage && el?.discountAmount) {
            return el?.discountAmount;
          }
          return 0;
        }));
      }
      if (serviceDiscount || installationDiscount) {
        const discountPercentage = +serviceDiscount > +installationDiscount ? serviceDiscount : installationDiscount;
        if (discountPercentage > 0 && discountPercentage <= 100) {
          otherParams['discountPercentage'] = discountPercentage;
        }
      }


      this.crudService.get(ModulesBasedApiSuffix.SALES, SalesModuleApiSuffixModels.DOA_LEVEL_PRICE_ADJUSTMENT, undefined, false, prepareRequiredHttpParams(otherParams)).subscribe(resp => {
        this.doaDetails = resp.resources;
        if (this.doaDetails?.doaApprovalLevel) {
          this.doaDetails['doaFullDetail'] = `${this.doaDetails.doaApprovalLevel + " ( " + this.doaDetails['doaApprovalLevelName']} )`;
        }
        this.rxjsService.setGlobalLoaderProperty(false)
      });
    }, 500);
  }

  submit() {
    let isLss = this.priceAdjustmentForm.get('isLSS').value
    let isCamp = this.priceAdjustmentForm.get('isCamp').value
    let isReward = this.priceAdjustmentForm.get('isReward').value
    let isGene = this.priceAdjustmentForm.get('isGene').value
    let rewardId = this.priceAdjustmentForm.get('rewardId').value
    this.priceAdjustmentForm.get('isCampaign').setValue(isCamp)
    this.priceAdjustmentForm.get('isGeneralDiscount').setValue(isGene)
    this.priceAdjustmentForm.get('isLSSPreferentialRate').setValue(isLss)
    this.priceAdjustmentForm.get('isRewardPartnership').setValue(isReward)
    this.priceAdjustmentForm.get('rewardPartnershipId').setValue(rewardId)
    this.priceAdjustmentForm.get('createdUserId').setValue(this.loggedInUserData.userId)
    if (isLss) {
      this.priceAdjustmentForm.get('rewardPartnershipId').setValue(null)
      this.priceAdjustmentForm.get('rewardPartnershipRefNo').setValue(null)
    }
    if (this.priceAdjustmentForm.invalid) {
      return;
    }
    let obj = { ...this.priceAdjustmentForm.getRawValue() };
    if (!obj.leadServicePriceAdjustments && !obj.leadItemPriceAdjustments.length) {
      this.snackbarService.openSnackbar("Service price or ItemPrice either one is mandatory", ResponseMessageTypes.WARNING);
      return;
    }
    if (obj.leadServicePriceAdjustments.length == 0 && obj.leadItemPriceAdjustments.length == 0) {
      this.snackbarService.openSnackbar("Service price or ItemPrice either one is mandatory", ResponseMessageTypes.WARNING);
      return;
    }
    obj.rewardPartnershipRefNo = obj.rewardPartnershipRefNo ? obj.rewardPartnershipRefNo : null;
    obj.doaLevel = this.doaDetails?.doaApprovalLevel;
    obj.doaLevelConfigId = this.doaDetails?.doaLevelConfigId;
    let leadServicePriceAdjustments = obj.leadServicePriceAdjustments.filter(e => e.discountAmount != "")
    let leadItemPriceAdjustments = obj.leadItemPriceAdjustments.filter(e => e.discountAmount != "")
    obj.leadServicePriceAdjustments = leadServicePriceAdjustments;
    obj.leadItemPriceAdjustments = leadItemPriceAdjustments;
    obj.quotationVersionId = this.detailsData?.quotationVersionId;
    obj['isConfirmGenerateQuotation'] = true;
    obj.isFixedContractFee = obj?.isFixedContractFee || false;
    obj.isWaiverAdminFee = obj?.isWaiverAdminFee || false;
    obj.isWaiverProRateCharges = obj?.isWaiverProRateCharges || false;
    obj['isWaiverNetworkFee'] = this.isWaiverNetworkFee || false;
    this.httpCancelService.cancelPendingRequestsOnFormSubmission();
    this.rxjsService.setFormChangeDetectionProperty(true);
    this.reusablePrimeNGTableFeatureService.openDynamicConfirmByMessageDialog(`Generate Quote with Adjusted Cost<br>
    Are you sure you want to Generate Quote with Adjusted Cost?`).onClose?.subscribe(dialogResult => {
      if (dialogResult) {
        this.crudService.create(ModulesBasedApiSuffix.SALES, SalesModuleApiSuffixModels.SALES_API_PRICE_ADJUSTMENTS, obj).subscribe((response: IApplicationResponse) => {
          if (response.isSuccess && response.statusCode == 200) {
            this.redirectToTab(btnActionTypes.SUBMIT);
          }
        });
      }
    });
  }

  redirectToTab(type?: btnActionTypes | string): void {
    if (type === btnActionTypes.SUBMIT || type === btnActionTypes.CANCEL) {
      if (this.leadHeaderData.fromUrl == 'Leads List') {
        this.router.navigate(["sales/task-management/leads/lead-items/quotes-info"], { queryParams: { leadId: this.leadHeaderData.leadId } });
      }
      else {
        this.router.navigate(["/my-tasks/my-sales/my-leads/lead-items/quotes-info"], { queryParams: { leadId: this.leadHeaderData.leadId } });
      }
    }
  }

  ngOndestroy() {
    this.rxjsService.setGlobalLoaderProperty(false);
  }
}
